// webpack 5 native Worker support - no need for worker-loader
import { Unsubscribe } from '@reduxjs/toolkit';

import { inits } from '@common/models';
import type { Types } from '@common/models';
import Sequence from '@common/Sequence';
import { generateUniqueKey, generateUniqueKeySeparator } from '@common/utils';
import { ApiStore, createApiStore } from '@api-client/redux/store';
import ApiState from '@api-client/state';
import { Pid, TuneId, Status, PostMessage, statusTunning } from '.';
import { createImportScriptsWorker } from '@api-client/wssWorker/createImportScriptsWorker';
import { createWssWorker } from '@api-client/wssWorker/createWorker';
import ChModel from '@common/models/Ch';
import type { Post } from '@common/models/Post';

export type CommandPayloadMap = {
  tune: { sessionId?: string; success?: boolean; error?: string };
  untune: { success?: boolean; error?: string };
  fetchRank: Record<string, never>;
  fetchPosts: Record<string, never>;
  fetchChDetail: Record<string, never>;
  discoverCh: { digChs?: string[]; errors?: string[] };
  digCh: { digChs?: string[]; errors?: string[] };
  subscribeDiscoveredChs: { digChs?: string[]; errors?: string[] };
  post: { success?: boolean; error?: string };
  deletePost: { success?: boolean; error?: string };
  trackSession: { success?: boolean; saved?: number; errors?: string[] };
  typing: Record<string, never>;
};

export type CommandRequestMap = {
  tune: { bootOption: Types['BootOption'] };
  untune: { tuneCh: Types['Ch'] };
  fetchRank: { tuneCh: Types['Ch'] };
  fetchPosts: { tuneCh: Types['Ch'] };
  fetchChDetail: { tuneCh: Types['Ch'] };
  discoverCh: {
    tuneCh: Types['Ch'];
    maxDepth?: number;
    maxLinksPerPage?: number;
    maxTotalLinks?: number;
    basicAuth?: { user: string; pass: string };
  };
  digCh: {
    tuneCh: Types['Ch'];
    maxDepth?: number;
    maxLinksPerPage?: number;
    maxTotalLinks?: number;
    subscribeDiscovered?: boolean;
    basicAuth?: { user: string; pass: string };
  };
  subscribeDiscoveredChs: { digChs: string[] };
  post: { posts: Post[] };
  deletePost: { uid: string; replyToId: string; content: string; connection: string };
  trackSession: { events: any[] };
  typing: { isTyping: boolean; userName?: string; connection?: string };
};

export type CommandMethod = keyof CommandRequestMap;

export type ResponseType<TPayload = Record<string, never>> = {
  state: ApiState;
  tuneId: string;
  payload?: TPayload;
};

export type Options = {
  isTuneSameCh?: boolean;
  isTuneMultiCh?: boolean;
  isDebug?: boolean;
};

export type RequiredOptions = Required<Options>;

const optionsInit: Options = {
  isTuneSameCh: false,
  isTuneMultiCh: false,
  isDebug: true,
};

type Uid = string;
type OnChangeState = (state: ApiState) => void;

type PendingRequest = {
  resolve: (response: ResponseType<any>) => void;
  reject: (error: Error) => void;
  timeoutId: NodeJS.Timeout;
  method: string;
};

type TuneIds = {
  [tuneId: TuneId]: {
    pendingRequests: Map<Pid, PendingRequest>; // 並行リクエスト管理
    fireAndForgetPids: Set<Pid>; // 応答を待たない送信のPID管理
    store: ApiStore;
    wssWorker: Worker;
    status: Status;
    permanentUnsubscribe: Unsubscribe; // 永続購読用
  };
};

export type TuneParams = Types['TuneProjection'] | OnChangeState;

type GetGeneratePidErrorCode = string;

const separator = generateUniqueKeySeparator;

// 但し、chromeで専用ワーカー167件に達すると強制的にエラー コード: 5が発生する
export const limitPidCnt = 1000;

export class StoresDispatcher {
  public uid: Uid;
  private useTuneId: TuneId;
  private tuneIds: TuneIds;
  private options: Options;
  private callback: (apiStates: ApiState[]) => void;
  constructor(options: Options = optionsInit) {
    this.getGeneratePidErrorCode = this.getGeneratePidErrorCode.bind(this);
    this.postMessage = this.postMessage.bind(this);
    this.onError = this.onError.bind(this);

    this.uid = generateUniqueKey('uid');
    this.useTuneId = '';
    this.tuneIds = {};
    this.options = options;
    this.callback = () => {};

    // public api endpoint.
    this.getTunePid = this.getTunePid.bind(this);
    this.getTuneIds = this.getTuneIds.bind(this);
    this.getState = this.getState.bind(this);
    this.tune = this.tune.bind(this);
    this.untune = this.untune.bind(this);
    this.fetchRank = this.fetchRank.bind(this);
    this.fetchPosts = this.fetchPosts.bind(this);
    this.fetchDetail = this.fetchDetail.bind(this);
    this.discoverCh = this.discoverCh.bind(this);
    this.digCh = this.digCh.bind(this);
    this.subscribeDiscoveredChs = this.subscribeDiscoveredChs.bind(this);
    this.post = this.post.bind(this);
    this.onStates = this.onStates.bind(this);
  }

  public getTuneIds(): string[] {
    return Object.keys(this.tuneIds);
  }

  public getState(tuneId?: string): ApiState {
    const tuneIdKey = tuneId ?? this.useTuneId;
    const config = this.tuneIds[tuneIdKey];

    if (!config) {
      const error = new Error(`TuneId not found: ${tuneIdKey}`);
      if (this.options.isDebug) {
        console.warn(error);
      }
      throw error;
    }

    return config.store.getState() as ApiState;
  }

  private async getResponse<TPayload = Record<string, never>>(tuneId = this.useTuneId): Promise<ResponseType<TPayload>> {
    return new Promise((resolve) => {
      const state = this.getState(tuneId);
      resolve({ state, tuneId });
    });
  }

  /**
   * チャンネルに接続し、WebSocketストリームを確立する
   *
   * @param connection - チャンネル接続パス
   * @param params - TuneProjectionまたはコールバック関数
   * @param _onChangeState - 状態変更コールバック（paramsが関数でない場合）
   * @returns Promise<ResponseType> - 接続結果とtuneId
   */
  public async tune(
    connection: string,
    params: TuneParams = inits.tuneOption,
    _onChangeState?: (apiState: ApiState) => void,
    rankScope?: string,
    apiKey?: string,
    host?: string,
    protocol?: string,
    isObserver?: boolean
  ): Promise<ResponseType<CommandPayloadMap['tune']>> {
    const generatePidErrorCode = this.getGeneratePidErrorCode(connection);
    if (generatePidErrorCode === '') {
      const onChangeState = resolveOnChangeState(params, _onChangeState);

      this.useTuneId = this.getTunePid(connection);
      const currentTuneId = this.useTuneId;
      // webpack 5 は new Worker(new URL(...)) パターンを検出して Worker バンドルを生成
      // 同一式内に書く必要がある（変数経由だとアセット参照のみになりバンドルされない）
      // クロスオリジン (admin.talkn.io → api.talkn.io) では SecurityError になるため
      // Blob Worker + importScripts でフォールバック
      let wssWorker: Worker;
      try {
        wssWorker = createWssWorker();
      } catch (error) {
        if (error instanceof DOMException && error.name === 'SecurityError') {
          const workerUrl = (error as Error).message.match(/Script at '(.+?)'/)?.[1];
          if (!workerUrl) throw error;
          wssWorker = createImportScriptsWorker(new URL(workerUrl));
        } else {
          throw error;
        }
      }

      this.tuneIds[currentTuneId] = {
        pendingRequests: new Map(),
        fireAndForgetPids: new Set(),
        store: createApiStore(),
        wssWorker,
        status: statusTunning,
        permanentUnsubscribe: () => {},
      };

      // boot wssWorker.
      this.tuneIds[currentTuneId].wssWorker.onerror = this.onError;
      this.tuneIds[currentTuneId].wssWorker.onmessage = this.onMessage.bind(this);

      // 永続購読: 継続的なbroadcastイベントをonChangeStateに通知
      this.tuneIds[currentTuneId].permanentUnsubscribe = this.tuneIds[currentTuneId].store.subscribe(() => {
        const config = this.tuneIds[currentTuneId];
        if (config) {
          const apiState = config.store.getState() as ApiState;
          const type = apiState.logs?.[0];

          // BROADCASTイベントの場合のみonChangeStateを呼ぶ（継続的な通知）
          if (type && type.startsWith(Sequence.SERVER_TO_API_BROADCAST)) {
            onChangeState(apiState);

            const states = this.getTuneIds()
              .map((id) => this.tuneIds[id]?.store.getState() as ApiState)
              .filter(Boolean);
            this.callback(states);
          }
        }
      });

      const tuneOption = typeof params === 'object' ? params : inits.tuneOption;
      const bootOption = {
        ...inits.bootOption,
        connection,
        tuneOption,
        ...(apiKey ? { apiKey } : {}),
        ...(host ? { host } : {}),
        ...(protocol ? { protocol } : {}),
        ...(rankScope ? { rankScope } : {}),
        ...(isObserver ? { isObserver: true } : {}),
        tuneRequest: {
          tuneOption,
          ...(rankScope ? { rankScope } : {}),
        },
      };
      const apiState = new ApiState({ bootOption });
      return await this.postMessage({ tuneId: currentTuneId, method: 'tune', apiState });
    }
    if (this.options.isDebug) {
      console.warn(`Error: tune(${connection}) Message: ${generatePidErrorCode}`);
    }
    return await this.getResponse();
  }

  /**
   * 既存 Worker を再利用してチャンネルだけ切り替える。
   * untune → new Worker → Socket.IO handshake のサイクルを省略し、
   * Socket.IO 接続の差し替えのみ行う（Worker スレッドは維持）。
   */
  public async retune(
    tuneId: string,
    connection: string,
  ): Promise<ResponseType<CommandPayloadMap['tune']>> {
    if (!this.tuneIds[tuneId]) {
      return Promise.reject(new Error(`Invalid tuneId for retune: ${tuneId}`));
    }

    const bootOption = { ...inits.bootOption, connection };
    const apiState = new ApiState({ bootOption });
    return await this.postMessage({ tuneId, method: 'tune', apiState });
  }

  // connection | tuneId
  public async untune(prefixConnections: string | string[]): Promise<ResponseType<CommandPayloadMap['untune']>> {
    const connections = Array.isArray(prefixConnections) ? prefixConnections : [prefixConnections];
    const allTuneIds = this.getTuneIds();
    const untuneId = allTuneIds.find((tuneId) => {
      return connections.find((connection) => tuneId.startsWith(`con${separator}${connection}${separator}`));
    });

    if (untuneId) {
      const tuneCh = { ...inits.ch };
      const response = await this.postMessage({ tuneId: untuneId, method: 'untune', apiState: { tuneCh } });

      const tuneIds = Object.keys(this.tuneIds);
      const tuneCnt = tuneIds.length;
      const latestTuneId = tuneIds[tuneCnt - 1] ? tuneIds[tuneCnt - 1] : '';
      this.useTuneId = this.useTuneId === untuneId ? latestTuneId : this.useTuneId;

      return response;
    } else {
      if (this.options.isDebug) {
        console.warn(`Error: untune(${prefixConnections})`, untuneId);
      }
    }
    return await this.getResponse();
  }

  // 共通のfetchデータメソッド
  private async fetchData<TMethod extends 'fetchRank' | 'fetchPosts' | 'fetchChDetail' | 'digCh' | 'discoverCh'>(
    method: TMethod,
    tuneId?: string
  ): Promise<ResponseType<CommandPayloadMap[TMethod]>> {
    const targetTuneId = tuneId || this.useTuneId;
    const connection = ChModel.getConnectionFromTuneId(targetTuneId);
    const state = this.tuneIds[targetTuneId].store.getState() as ApiState;
    const tuneCh = { ...state.tuneCh, connection };
    return await this.postMessage<TMethod>({ tuneId: targetTuneId, method, apiState: { tuneCh } });
  }

  public async fetchRank(tuneId?: string): Promise<ResponseType<CommandPayloadMap['fetchRank']>> {
    return this.fetchData('fetchRank', tuneId);
  }

  public async fetchPosts(tuneId?: string): Promise<ResponseType<CommandPayloadMap['fetchPosts']>> {
    return this.fetchData('fetchPosts', tuneId);
  }

  public async fetchDetail(tuneId?: string): Promise<ResponseType<CommandPayloadMap['fetchChDetail']>> {
    return this.fetchData('fetchChDetail', tuneId);
  }

  public async discoverCh(tuneId?: string, params?: {
    maxDepth?: number;
    maxLinksPerPage?: number;
    maxTotalLinks?: number;
    basicAuth?: {
      user: string;
      pass: string;
    };
  }): Promise<ResponseType<CommandPayloadMap['discoverCh']>> {
    const targetTuneId = tuneId || this.useTuneId;
    const connection = ChModel.getConnectionFromTuneId(targetTuneId);
    const state = this.tuneIds[targetTuneId].store.getState() as ApiState;
    const tuneCh = { ...state.tuneCh, connection };
    return await this.postMessage<'discoverCh'>({
      tuneId: targetTuneId,
      method: 'discoverCh',
      apiState: { tuneCh, ...params }
    });
  }

  public async digCh(tuneId?: string, params?: {
    maxDepth?: number;
    maxLinksPerPage?: number;
    maxTotalLinks?: number;
    subscribeDiscovered?: boolean;
    basicAuth?: {
      user: string;
      pass: string;
    };
  }): Promise<ResponseType<CommandPayloadMap['digCh']>> {
    const targetTuneId = tuneId || this.useTuneId;
    const connection = ChModel.getConnectionFromTuneId(targetTuneId);
    const state = this.tuneIds[targetTuneId].store.getState() as ApiState;
    const tuneCh = { ...state.tuneCh, connection };
    return await this.postMessage<'digCh'>({
      tuneId: targetTuneId,
      method: 'digCh',
      apiState: { tuneCh, ...params }
    });
  }

  public async subscribeDiscoveredChs(
    tuneId?: string,
    connections?: string[]
  ): Promise<ResponseType<CommandPayloadMap['subscribeDiscoveredChs']>> {
    const targetTuneId = tuneId || this.useTuneId;
    return await this.postMessage<'subscribeDiscoveredChs'>({
      tuneId: targetTuneId,
      method: 'subscribeDiscoveredChs',
      apiState: { digChs: connections || [] },
    });
  }

  public trackCardEvent(
    tuneId: string,
    eventType: 'card_view' | 'card_click',
    ch: string,
    tuneConnection: string,
    linkUrl?: string,
    trackingUserId?: string,
    socketSessionId?: string
  ): void {
    if (!this.tuneIds[tuneId]) return;

    const eventId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);

    const payload =
      eventType === 'card_click'
        ? { type: 'card_click' as const, ch, tuneConnection, linkUrl: linkUrl || '', userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown' }
        : { type: 'card_view' as const, ch, tuneConnection, userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown' };

    const event = {
      eventId,
      sessionId: trackingUserId ?? tuneId,
      trackingUserId: trackingUserId ?? tuneId,
      socketSessionId,
      timestamp: Date.now(),
      eventType,
      payload,
    };

    this.sendFireAndForgetTrackingEvents(tuneId, [event], `card-${eventId}`);
  }

  /**
   * ページビューイベントを送信する（CVRコンバージョン追跡用）
   *
   * カードクリック後のチャンネル訪問を記録し、コンバージョンを計測する。
   * tuneが完了した後にメインスレッドから呼ばれる。
   *
   * @param tuneId - WebSocket接続ID
   * @param ch - 訪問したチャンネル（例: "/manga/"）
   * @param userId - 匿名ユーザーID（localStorageから取得、クロスページ追跡用）
   */
  public trackPageView(tuneId: string, ch: string, userId?: string): void {
    if (!this.tuneIds[tuneId]) return;

    const eventId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);

    const event = {
      eventId,
      sessionId: userId ?? tuneId,
      trackingUserId: userId ?? tuneId,
      timestamp: Date.now(),
      eventType: 'page_view' as const,
      payload: {
        type: 'page_view' as const,
        path: '',       // チャンネルベースの追跡のためpathは空
        title: '',
        referrer: '',
        ch,
        tuneConnection: ch,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      },
    };

    this.sendFireAndForgetTrackingEvents(tuneId, [event], `pv-${eventId}`);
  }

  /**
   * トラッキングイベントをch-serverに送信する（fire-and-forget）
   * SessionTrackerからの呼び出し用
   */
  public async sendTrackingEvents(
    tuneId: string,
    events: any[]
  ): Promise<ResponseType<CommandPayloadMap['trackSession']>> {
    if (!this.tuneIds[tuneId] || events.length === 0) {
      const state = this.tuneIds[tuneId]?.store.getState() as ApiState | undefined;
      return Promise.resolve({
        tuneId,
        state: state ?? new ApiState(),
        payload: { success: false, saved: 0, errors: ['No active tuneId or events'] },
      });
    }

    return this.postMessage<'trackSession'>({
      tuneId,
      method: 'trackSession',
      apiState: { events },
    });
  }

  private sendFireAndForgetTrackingEvents(tuneId: string, events: any[], pid: string): void {
    const config = this.tuneIds[tuneId];
    if (!config || events.length === 0) {
      return;
    }

    config.fireAndForgetPids.add(pid);
    config.wssWorker.postMessage({
      pid,
      tuneId,
      method: 'trackSession',
      apiState: { events },
    });
  }

  public async post(content: string, tuneId?: string, userName?: string, userAvatar?: string, replyToId?: string): Promise<ResponseType<CommandPayloadMap['post']>> {
    const targetTuneId = this.resolveActiveTuneId(tuneId || this.useTuneId);
    const connection = ChModel.getConnectionFromTuneId(targetTuneId);
    const post: Record<string, unknown> = { connection, content, uid: this.uid };
    if (userName) post.userName = userName;
    if (userAvatar) post.userAvatar = userAvatar;
    if (replyToId) post.replyToId = replyToId;
    const posts = [Object.assign({}, post)] as Post[]; // モデルはRedux.stateが許容しないので、オブジェクトに変換
    return await this.postMessage<'post'>({ tuneId: targetTuneId, method: 'post', apiState: { posts } });
  }

  public async deletePost(uid: string, replyToId: string, content: string, tuneId?: string): Promise<ResponseType<CommandPayloadMap['deletePost']>> {
    const targetTuneId = this.resolveActiveTuneId(tuneId || this.useTuneId);
    const connection = ChModel.getConnectionFromTuneId(targetTuneId);
    return await this.postMessage<'deletePost'>({ tuneId: targetTuneId, method: 'deletePost', apiState: { uid, replyToId, content, connection } });
  }

  public sendTyping(isTyping: boolean, tuneId?: string, userName?: string): void {
    const targetTuneId = this.resolveActiveTuneId(tuneId || this.useTuneId);
    const config = this.tuneIds[targetTuneId];
    if (!config) return;
    const connection = ChModel.getConnectionFromTuneId(targetTuneId);
    const pid = generateUniqueKey('typing');
    config.fireAndForgetPids.add(pid);
    config.wssWorker.postMessage({
      pid,
      tuneId: targetTuneId,
      method: 'typing',
      apiState: { isTyping, userName, connection },
    });
  }

  public async onStates(callback: (apiStates: ApiState[]) => void) {
    this.callback = callback;
  }

  /**
   * 特定のtuneIdに対する状態変更を監視する
   *
   * @param tuneId - 監視対象のtuneId
   * @param callback - 状態変更時に呼ばれるコールバック
   * @returns unsubscribe関数
   */
  public subscribeToTuneId(tuneId: string, callback: (apiState: ApiState) => void): () => void {
    if (!this.tuneIds[tuneId]) {
      throw new Error(`TuneId ${tuneId} not found`);
    }

    // Redux store監視を設定
    // BROADCASTイベントのみをコールバックに通知（REQUESTイベントは除外）
    const unsubscribe = this.tuneIds[tuneId].store.subscribe(() => {
      if (this.tuneIds[tuneId]) {
        const apiState = this.tuneIds[tuneId].store.getState() as ApiState;
        const type = apiState.logs?.[0];

        // BROADCASTイベントまたはEMITレスポンスの場合のみコールバックを呼ぶ
        if (type && (type.startsWith(Sequence.SERVER_TO_API_BROADCAST) || type.startsWith(Sequence.SERVER_TO_API_EMIT))) {
          callback(apiState);
        }
      }
    });

    return unsubscribe;
  }

  private async postMessage<TMethod extends CommandMethod>(
    params: {
      tuneId: TuneId;
      method: TMethod;
      apiState: CommandRequestMap[TMethod];
    }
  ): Promise<ResponseType<CommandPayloadMap[TMethod]>> {
    const method = params.method;
    const apiState = params.apiState;
    const tuneId = this.resolveActiveTuneId(params.tuneId);

    // 事前検証：tuneIdが存在するか
    if (!this.tuneIds[tuneId]) {
      return Promise.reject(new Error(`Invalid tuneId: ${tuneId}`));
    }

    return new Promise((resolve, reject) => {
      const pid = generateUniqueKey(`pid`);

      // タイムアウト設定（20秒）
      const timeoutId = setTimeout(() => {
        // タイムアウト時にpendingRequestsから削除
        if (this.tuneIds[tuneId] && this.tuneIds[tuneId].pendingRequests.has(pid)) {
          this.tuneIds[tuneId].pendingRequests.delete(pid);
        }
        reject(new Error(`postMessage timeout for method: ${method}, tuneId: ${tuneId}, pid: ${pid}`));
      }, 20000);

      // PID別リクエスト管理に登録
      if (this.tuneIds[tuneId]) {
        this.tuneIds[tuneId].pendingRequests.set(pid, {
          resolve,
          reject,
          timeoutId,
          method,
        });

        try {
          const type = `${Sequence.API_TO_SERVER_REQUEST}${method}`;
          this.tuneIds[tuneId].store.dispatch({ ...apiState, type });
          this.tuneIds[tuneId].wssWorker.postMessage({ pid, tuneId, method, apiState });
        } catch (err) {
          // エラー時にクリーンアップ
          if (this.tuneIds[tuneId] && this.tuneIds[tuneId].pendingRequests.has(pid)) {
            clearTimeout(timeoutId);
            this.tuneIds[tuneId].pendingRequests.delete(pid);
          }
          reject(err);
        }
      } else {
        clearTimeout(timeoutId);
        reject(new Error(`TuneId ${tuneId} was deleted during postMessage`));
      }
    });
  }

  private onMessage(e: MessageEvent): void {
    const { currentTarget, data } = e;
    if (currentTarget instanceof Worker) {
      const { pid, tuneId, method, apiState, serverToApiType } = data;

      if (tuneId && method && apiState && serverToApiType) {
        if (!this.tuneIds[tuneId]) {
          if (this.options.isDebug) {
            console.warn('[StoresDispatcher] Unknown tuneId', tuneId);
          }
          return;
        }

        // Reduxにdispatch
        apiState.type = `${serverToApiType}${method}`;
        this.tuneIds[tuneId].store.dispatch(apiState);

        // PID別リクエスト解決
        const pendingRequest = pid ? this.tuneIds[tuneId].pendingRequests.get(pid) : undefined;
        const fireAndForgetPids = this.tuneIds[tuneId].fireAndForgetPids;
        if (pendingRequest) {
          clearTimeout(pendingRequest.timeoutId);
          this.tuneIds[tuneId].pendingRequests.delete(pid);

          // ToServer 側から伝播されたサーバエラー（API_KEY_REQUIRED 等）は即時 reject
          const errorMessage = (apiState as { error?: unknown })?.error;
          if (typeof errorMessage === 'string' && errorMessage.length > 0) {
            pendingRequest.reject(new Error(errorMessage));
          } else {
            const state = this.tuneIds[tuneId].store.getState() as ApiState;
            pendingRequest.resolve({
              tuneId,
              state,
              payload: apiState as Record<string, unknown>,
            });
          }
        } else if (pid && fireAndForgetPids?.has(pid)) {
          fireAndForgetPids.delete(pid);
        } else if (pid && this.options.isDebug) {
          console.warn('[StoresDispatcher] No pending request for PID', {
            pid,
            tuneId,
            method,
            availablePids: Array.from(this.tuneIds[tuneId].pendingRequests.keys()),
          });
        }

        if (method === 'untune' && serverToApiType !== Sequence.SERVER_TO_API_BROADCAST) {
          this.cleanupTuneId(tuneId);
        }
      }
    }
  }

  private cleanupTuneId(tuneId: TuneId, reason?: string) {
    const config = this.tuneIds[tuneId];
    if (!config) {
      return;
    }

    try {
      config.permanentUnsubscribe();
    } catch (error) {
      if (this.options.isDebug) {
        console.warn('[StoresDispatcher] permanentUnsubscribe failed', error);
      }
    }

    config.pendingRequests.forEach((pending) => {
      clearTimeout(pending.timeoutId);
      if (pending.method === 'trackSession') {
        // trackSession は best-effort — cleanup 時は静かに resolve する
        pending.resolve({ method: 'trackSession', payload: { success: false, saved: 0, errors: [] } } as any);
      } else {
        pending.reject(new Error(reason || `TuneId ${tuneId} was closed before resolving ${pending.method}`));
      }
    });
    config.pendingRequests.clear();
    config.fireAndForgetPids?.clear();

    if (config.wssWorker && typeof config.wssWorker.terminate === 'function') {
      config.wssWorker.terminate();
    }

    delete this.tuneIds[tuneId];

    if (this.useTuneId === tuneId) {
      const remaining = this.getTuneIds();
      this.useTuneId = remaining[remaining.length - 1] || '';
    }
  }

  private onError(e: ErrorEvent): void {
    console.warn(e);
  }

  private resolveActiveTuneId(tuneId: TuneId): TuneId {
    if (this.tuneIds[tuneId]) {
      return tuneId;
    }

    const connection = ChModel.getConnectionFromTuneId(tuneId);
    const fallbackTuneId = this.getTuneIds().find((id) =>
      id.startsWith(`con${separator}${connection}${separator}`)
    );

    if (fallbackTuneId) {
      if (this.options.isDebug) {
        console.warn('[StoresDispatcher] Falling back to active tuneId', {
          requestedTuneId: tuneId,
          fallbackTuneId,
        });
      }
      return fallbackTuneId;
    }

    return tuneId;
  }

  private getTunePid(connection: string): string {
    const { isTuneSameCh, isTuneMultiCh } = this.options;
    const uniqueKey = generateUniqueKey(`con${separator}${connection}`);
    const newTunePid = uniqueKey + separator + this.uid;
    const tuneIdCnt = this.getTuneIds().length;

    if (isTuneSameCh && isTuneMultiCh) {
      return newTunePid;
    } else if (!isTuneSameCh && isTuneMultiCh) {
      // 同じchは新しいtuneIdを許容しないが、異なるchは許容する
      const tunedSameCh = Object.keys(this.tuneIds).find((tuneId) =>
        Boolean(tuneId.startsWith(`con${separator}${connection}${separator}`))
      );
      return tunedSameCh ? tunedSameCh : newTunePid;
    } else if (isTuneSameCh && !isTuneMultiCh) {
      // 同じchは新しいtuneIdを許容するが、異なるchは許容しない
      const otherCh = Object.keys(this.tuneIds).find((tuneId) => !Boolean(tuneId.startsWith(`con${separator}${connection}${separator}`)));
      return otherCh ? otherCh : newTunePid;
    } else {
      return tuneIdCnt === 1 ? Object.keys(this.tuneIds)[0] : newTunePid;
    }
  }

  private getGeneratePidErrorCode(connection: string): GetGeneratePidErrorCode {
    const { isTuneSameCh, isTuneMultiCh } = this.options;
    const tuneIdCnt = this.getTuneIds().length;

    if (tuneIdCnt === 0) {
      return '';
    } else {
      if (tuneIdCnt >= limitPidCnt) {
        return 'TUNE LIMIT';
      }

      if (isTuneSameCh && isTuneMultiCh) {
        return '';
      } else if (!isTuneSameCh && isTuneMultiCh) {
        const isError = Boolean(Object.keys(this.tuneIds).find((tuneId) => tuneId.startsWith(`con${separator}${connection}${separator}`)));
        return isError ? 'BAD SAME CONNECTION' : '';
      } else if (isTuneSameCh && !isTuneMultiCh) {
        const isError = Boolean(Object.keys(this.tuneIds).find((tuneId) => !tuneId.startsWith(`con${separator}${connection}${separator}`)));
        return isError ? 'BAD MULTI CONNECTION#1' : '';
      } else {
        const isError = Boolean(tuneIdCnt >= 1);
        return isError ? 'BAD MULTI CONNECTION#2' : '';
      }
    }
  }
}

const createStoresDispatcher = async (options?: Options): Promise<StoresDispatcher> => {
  const storesDispatcher = new StoresDispatcher(options);
  return storesDispatcher;
};

export { createStoresDispatcher };
export default createStoresDispatcher;

/***************
 * Functions
 ***************/

/**
 * onChangeState生成ヘルパー関数
 * paramsが関数の場合はそれを使用、_onChangeStateが関数の場合はそれを使用、どちらでもない場合は空関数を返す
 */
function resolveOnChangeState(params: TuneParams | undefined, _onChangeState: OnChangeState | undefined): OnChangeState {
  if (typeof params === 'function') {
    return params;
  }
  if (typeof _onChangeState === 'function') {
    return _onChangeState;
  }
  return () => {};
}
